import { Fragment, lazy, Suspense, useState } from 'react';
import CustomLayout from '../components/custom-layout';
import Starfield from '../backgrounds/starfield';
import Section from '../components/portfolio/section';
import SbButton from '../components/sb-button';
import { EModalSize } from '../enum/modal';
import Modal from '../components/modal';
import sandboxJSON from '../data/sandbox.json';
import { SandboxGroup, SandboxType } from '../types/sandbox';
import classes from './sandbox.module.css';

type ComponentProps = {
  [key: string]: any,
}

const Components: ComponentProps = {
  a11y: lazy(() => import('../components/sandbox/a11y')),
  age: lazy(() => import('../components/sandbox/age-check')),
  bounce: lazy(() => import('../components/sandbox/bounce')),
  cave: lazy(() => import('../components/sandbox/cave')),
  converter: lazy(() => import('../components/sandbox/converter')),
  cube3d: lazy(() => import('../components/sandbox/cube3d')),
  earth3d: lazy(() => import('../components/sandbox/earth-3d/earth-3d')),
  gravity: lazy(() => import('../components/sandbox/gravity')),
  grid3d: lazy(() => import('../components/sandbox/grid-3d')),
  maze3d: lazy(() => import('../components/sandbox/maze-3d')),
  renders: lazy(() => import('../components/sandbox/renders')),
  scene3d: lazy(() => import('../components/sandbox/scene-3d')),
  simpleTrig: lazy(() => import('../components/sandbox/simple-trig')),
};

const Sandbox = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [compId, setCompId] = useState<string>('');
  const [showImage, setShowImage] = useState<number | null>(null);
  const [modalWidth, setModalWidth] = useState<string>('lg');
  const [modalHeight, setModalHeight] = useState<string>('md');
  const { sandboxData } = sandboxJSON;

  const Component = Components[compId];

  const getModalSize = (size: string) => {
    switch (size) {
      case 'sm':
        return EModalSize.sm;
      case 'md':
        return EModalSize.md;
      case 'lg':
        return EModalSize.lg;
      case 'xl':
        return EModalSize.xl;
      default:
        return EModalSize.md;
    };
  };

  return (
    <>
      <CustomLayout
        background={<Starfield />}
      >
        <Section
          title="Sandbox"
          collective="Sandbox"
          extraClasses={classes.section}
        >
          <h2 className={classes.h2}>Intro</h2>
          <div className={classes.container}>
            <p>
              This is a loose collection of experiments and ideas that I have had over the years. Exploring features,
              testing functionality and investigating programmatic solutions, these files have been sitting on my hard
              drive (in some cases for many years), it's about time I move them out and blow the dust off. This allows
              me to innovate and explore in a somewhat disconnected approach.
            </p>
            <p>
              Please note that these examples aren't optimised or necessarily accessible and probably viewed best on
              a desktop display. Cheers.
            </p>
          </div>

          {sandboxData.map((sandboxGroup: SandboxGroup) => (
            <Fragment key={sandboxGroup.title}>
              <h2 className={classes.h2}>{sandboxGroup.title}</h2>

              <div className={classes.container}>
                {sandboxGroup.children.map((sandbox: SandboxType, index: number) => (
                  <SbButton
                    key = {sandbox.id}
                    filename = {sandbox.filename}
                    onClick={() => {
                      setModalWidth(sandbox.width ?? 'lg');
                      setModalHeight(sandbox.height ?? 'md');
                      setCompId(!sandbox.filename ? sandbox.id : 'renders');
                      setShowImage(sandbox.filename ? index : null);
                      setIsModalOpen(true);
                    }}
                    year={sandbox.year}
                  >
                    {sandbox.text}
                  </SbButton>
                ))}
              </div>
            </Fragment>
          ))}
        </Section>
      </CustomLayout>

      {isModalOpen && (
        <Modal
          onClose={() => { setIsModalOpen(false) }}
          width={getModalSize(modalWidth)}
          height={getModalSize(modalHeight)}
        >
          <Suspense fallback={<div>Loading...</div>}>
            <Component showImage={showImage} />
          </Suspense>
        </Modal>
      )}
    </>
  );
};

export default Sandbox;
